import {
  Box,
  CircularProgress,
  Container,
  Divider,
  Grid,
  Typography,
} from '@material-ui/core'
import { useEffect } from 'react'
import {
  Link,
  Navigate,
  useLocation,
  useNavigate,
  useParams,
} from 'react-router-dom'

import Button from '@/components/button'
import InputText from '@/components/input-text'
import { useUpdateDiagnosisById } from '@/hooks/mutations/use-update-diagnosis-by-id'
import { useGetProjectByID } from '@/hooks/queries/use-get-project-by-id'
import {
  EditFormData,
  useEditDiagnosisForm,
} from '@/pages/diagnosis-edit/context/form/use-edit-diagnosis-form'
import { routes } from '@/routes/config'
import { Culture, CultureLabel } from '@/types/culture'
import { ImageType, ImageTypeLabel } from '@/types/image-types'
import { Treatment, TreatmentLabel } from '@/types/treatment'

import { useStyles } from './styles'

function EditName() {
  const classes = useStyles()
  const location = useLocation()
  const navigate = useNavigate()

  const { id: demandId } = useParams<{ id: string }>()

  const state = location.state as {
    demandName: string
    project: { id: number; name: string; region_id?: number }
    culture: Culture
    treatment: Treatment
    flightDate: string
    imageType: ImageType
  }

  const {
    handleSubmit,
    register,
    formState: { errors },
  } = useEditDiagnosisForm({
    defaultValues: { demand_name: state?.demandName },
  })

  const { mutateAsync: submitEditedData, isLoading } = useUpdateDiagnosisById(
    demandId!,
  )

  const projectId = state?.project?.id
  const regionId = state?.project?.region_id

  const getRegionByIdQuery = useGetProjectByID(projectId, {
    enabled: !!projectId && !!regionId,
  })

  const region = getRegionByIdQuery.data?.regions.find(
    (region) => region.id === regionId,
  )

  async function onSubmit(data: EditFormData) {
    await submitEditedData({ name: data.demand_name })

    navigate(routes.EditDiagnosis('finish', demandId!), {
      state: {},
    })
  }

  useEffect(() => {
    const title = document.title
    document.title = 'Editar Diagnósticos'
    return () => {
      document.title = title
    }
  }, [])

  if (!location.state) {
    return <Navigate to={routes.Diagnoses()} replace />
  }

  return (
    <Container
      className={classes.main}
      component='form'
      onSubmit={handleSubmit(onSubmit)}
    >
      <Box>
        <Box>
          <Typography variant='h1'>Editar Diagnóstico</Typography>
          <Typography variant='h4'>Informações básicas</Typography>
          <Grid container spacing={1} className={classes.container}>
            <Grid item xs={12}>
              <InputText
                id='demand_name'
                label='Nome do diagnóstico'
                error={!!errors?.demand_name}
                helperText={errors?.demand_name?.message}
                InputLabelProps={{ shrink: true }}
                {...register('demand_name')}
              />
            </Grid>
            <Grid item xs={12}>
              <InputText
                disabled
                InputProps={{
                  readOnly: true,
                }}
                id='project'
                label={'Selecione o projeto'}
                InputLabelProps={{ shrink: true }}
                value={state.project.name}
              />
            </Grid>
            <Grid item xs={12}>
              <InputText
                disabled
                InputProps={{
                  endAdornment: region ? undefined : (
                    <CircularProgress color='inherit' size={18} />
                  ),
                }}
                id='region'
                label={'Selecione a região'}
                InputLabelProps={{ shrink: true }}
                value={region?.name || ''}
              />
            </Grid>
            <Grid item xs={12}>
              <InputText
                disabled
                id='culture'
                label={'Selecione a cultura'}
                InputLabelProps={{ shrink: true }}
                value={CultureLabel[state.culture]}
              />
            </Grid>
            <Grid item xs={12}>
              <InputText
                disabled
                id='treatment'
                label={'Selecione o diagnóstico'}
                InputLabelProps={{ shrink: true }}
                value={TreatmentLabel[state.treatment]}
              />
            </Grid>
            <Grid item xs={6}>
              <InputText
                disabled
                id='image_type'
                label={'Selecione o tipo de imagem'}
                InputLabelProps={{ shrink: true }}
                value={ImageTypeLabel[state.imageType]}
              />
            </Grid>
            <Grid item xs={6}>
              <InputText
                disabled
                id='flight_date'
                label={'Data do voo'}
                InputLabelProps={{ shrink: true }}
                value={new Date(state.flightDate).toLocaleDateString('pt-BR')}
              />
            </Grid>
          </Grid>
        </Box>
      </Box>
      <Divider />
      <Box className={classes.buttonContainer}>
        <Link to='/diagnoses'>
          <Button outlined>Cancelar</Button>
        </Link>
        <Button type='submit' loading={isLoading}>
          Salvar
        </Button>
      </Box>
    </Container>
  )
}

export default EditName
